/**
 * Flickering neon point light near the sign. Flicker is a deterministic
 * function of accumulated time (frame-rate independent): fast buzz, slow
 * swell, and hashed random dropouts. Finale modes: steady (won), dead (lost).
 * Owned by LIGHT; see CONTRACTS.md.
 */
import { PointLight, Scene } from 'three'
import type { Vec3 } from '../core/types'
import { lerp } from './blend'
import {
  NEON_BUZZ_DEPTH,
  NEON_BUZZ_HZ_A,
  NEON_BUZZ_HZ_B,
  NEON_COLOR,
  NEON_DEAD_LEVEL,
  NEON_DECAY,
  NEON_DISTANCE,
  NEON_DROPOUT_LEVEL,
  NEON_DROPOUT_PROBABILITY,
  NEON_DROPOUT_SEGMENT_HZ,
  NEON_INDOOR_SCALE,
  NEON_INTENSITY,
  NEON_POSITION,
  NEON_SWELL_DEPTH,
  NEON_SWELL_HZ
} from './constants'

export type NeonMode = 'flicker' | 'steady' | 'dead'

export interface NeonRig {
  readonly position: Vec3
  setMode(mode: NeonMode): void
  /** Advance flicker time and apply intensity. weight: 1 outdoor, 0 indoor. */
  update(dt: number, weight: number, dim: number): void
}

const HASH_SEED_A = 12.9898
const HASH_SEED_B = 43758.5453

const hash = (n: number): number => {
  const value = Math.sin(n * HASH_SEED_A) * HASH_SEED_B
  return value - Math.floor(value)
}

const flickerLevel = (time: number): number => {
  const segment = Math.floor(time * NEON_DROPOUT_SEGMENT_HZ)
  if (hash(segment) < NEON_DROPOUT_PROBABILITY) return NEON_DROPOUT_LEVEL
  const buzz =
    1 -
    NEON_BUZZ_DEPTH +
    NEON_BUZZ_DEPTH * Math.sin(time * NEON_BUZZ_HZ_A) * Math.sin(time * NEON_BUZZ_HZ_B)
  const swell = 1 - NEON_SWELL_DEPTH + NEON_SWELL_DEPTH * Math.sin(time * NEON_SWELL_HZ)
  return buzz * swell
}

export const createNeonRig = (scene: Scene): NeonRig => {
  const light = new PointLight(NEON_COLOR, NEON_INTENSITY, NEON_DISTANCE, NEON_DECAY)
  light.position.set(NEON_POSITION.x, NEON_POSITION.y, NEON_POSITION.z)
  light.castShadow = false
  scene.add(light)

  let mode: NeonMode = 'flicker'
  let time = 0

  const levelForMode = (): number => {
    if (mode === 'steady') return 1
    if (mode === 'dead') return NEON_DEAD_LEVEL
    return flickerLevel(time)
  }

  const update = (dt: number, weight: number, dim: number): void => {
    time += dt
    const zoneScale = lerp(NEON_INDOOR_SCALE, 1, weight)
    light.intensity = NEON_INTENSITY * levelForMode() * zoneScale * dim
  }

  return {
    position: NEON_POSITION,
    setMode: (next) => {
      mode = next
    },
    update
  }
}
